import { supabase, supabaseAdmin } from './supabaseClient'
import type {
  EquipmentSerial,
  EquipmentTypeSummary,
  EquipmentStatus,
  EquipmentTypeCounts,
  EquipmentTypeUpdateInput,
  EquipmentTypeCreateInput,
  EquipmentSerialCreateInput,
  EquipmentSerialStatusChange,
  EquipmentTypeSpecs,
  EquipmentTypeAsset,
  EquipmentTypeAccessory,
  EquipmentProduct,
  EquipmentProductCreateInput,
  EquipmentProductUpdateInput,
  EquipmentTypeDraft,
  EquipmentTypeDraftInput,
} from '@/types/inventory'

type EquipmentTypeRow = {
  equipment_type_id?: number
  id?: number
  type_key?: string
  name: string
  make: string
  model: string
  descriptor?: string | null
  category_id?: number | null
  category_name?: string | null
  is_accessory?: boolean
  status?: string | null
  products?: string[] | null
  specs?: EquipmentTypeSpecs | null
  assets?: EquipmentTypeAsset[] | null
  accessories?: EquipmentTypeAccessory[] | null
  available_count?: number
  reserved_count?: number
  checked_out_count?: number
  maintenance_count?: number
  retired_count?: number
  lost_count?: number
  total_count?: number
}

type EquipmentSerialRow = {
  id: number
  equipment_type_id: number
  serial: string | null
  status: EquipmentStatus
  condition?: string | null
  notes?: string | null
  last_status_change?: string | null
  created_at: string
  updated_at: string
}

function mapCounts(row: EquipmentTypeRow): EquipmentTypeCounts {
  const available = Number(row.available_count ?? 0)
  const reserved = Number(row.reserved_count ?? 0)
  const checkedOut = Number(row.checked_out_count ?? 0)
  const maintenance = Number(row.maintenance_count ?? 0)
  const retired = Number(row.retired_count ?? 0)
  const lost = Number(row.lost_count ?? 0)
  const total = Number(
    row.total_count ?? available + reserved + checkedOut + maintenance + retired + lost,
  )
  return { available, reserved, checkedOut, maintenance, retired, lost, total }
}

function mapType(row: EquipmentTypeRow): EquipmentTypeSummary {
  const id = Number(row.equipment_type_id ?? row.id)
  const key = row.type_key ?? `type_${id}`
  return {
    id,
    key,
    name: row.name,
    make: row.make,
    model: row.model,
    descriptor: row.descriptor ?? null,
    categoryId: row.category_id ?? null,
    categoryName: row.category_name ?? null,
    isAccessory: Boolean(row.is_accessory),
    status: (row.status as EquipmentTypeSummary['status']) ?? 'active',
    products: Array.isArray(row.products) ? row.products : [],
    specs: row.specs ?? null,
    assets: row.assets ?? [],
    accessories: row.accessories ?? [],
    counts: mapCounts(row),
  }
}

export async function fetchEquipmentTypeDetail(id: number): Promise<EquipmentTypeSummary> {
  const { data, error } = await supabase
    .from('equipment_types_v')
    .select(
      [
        'equipment_type_id',
        'type_key',
        'name',
        'make',
        'model',
        'descriptor',
        'category_id',
        'category_name',
        'is_accessory',
        'status',
        'products',
        'specs',
        'assets',
        'accessories',
        'available_count',
        'reserved_count',
        'checked_out_count',
        'maintenance_count',
        'retired_count',
        'lost_count',
        'total_count',
      ].join(','),
    )
    .eq('equipment_type_id', id)
    .single()
  if (error) throw error
  return mapType(data as unknown as EquipmentTypeRow)
}

function mapSerial(row: EquipmentSerialRow): EquipmentSerial {
  return {
    id: Number(row.id),
    equipmentTypeId: Number(row.equipment_type_id),
    serial: row.serial,
    status: row.status,
    condition: row.condition ?? null,
    notes: row.notes ?? null,
    lastStatusChange: row.last_status_change ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export async function listEquipmentTypes(): Promise<EquipmentTypeSummary[]> {
  const { data, error } = await supabase
    .from('equipment_types_v')
    .select(
      [
        'equipment_type_id',
        'type_key',
        'name',
        'make',
        'model',
        'descriptor',
        'category_id',
        'category_name',
        'is_accessory',
        'status',
        'products',
        'specs',
        'assets',
        'accessories',
        'available_count',
        'reserved_count',
        'checked_out_count',
        'maintenance_count',
        'retired_count',
        'lost_count',
        'total_count',
      ].join(','),
    )
    .order('name')
  if (error) throw error
  const rows = (data as unknown as EquipmentTypeRow[] | null) ?? []
  return rows.map(mapType)
}

export async function fetchEquipmentSerials(equipmentTypeId: number): Promise<EquipmentSerial[]> {
  const { data, error } = await supabase
    .from('equipment_serials_v')
    .select(
      'id,equipment_type_id,serial,status,condition,notes,last_status_change,created_at,updated_at',
    )
    .eq('equipment_type_id', equipmentTypeId)
    .order('serial')
  if (error) throw error
  const rows = (data as EquipmentSerialRow[] | null) ?? []
  return rows.map(mapSerial)
}

export async function updateEquipmentType(id: number, payload: EquipmentTypeUpdateInput) {
  const client = supabaseAdmin ?? supabase
  const patch: Record<string, unknown> = {}
  if (payload.name !== undefined) patch.name = payload.name
  if (payload.make !== undefined) patch.make = payload.make
  if (payload.model !== undefined) patch.model = payload.model
  if (payload.descriptor !== undefined) patch.descriptor = payload.descriptor
  if (payload.isAccessory !== undefined) patch.is_accessory = payload.isAccessory
  if (payload.categoryId !== undefined) patch.category_id = payload.categoryId
  if (payload.status !== undefined) patch.status = payload.status
  if (payload.products !== undefined) patch.products = payload.products

  if (!Object.keys(patch).length) return
  const { error } = await client.from('app_equipment_types').update(patch).eq('id', id)
  if (error) throw error
}

export async function deleteEquipmentType(id: number) {
  const client = supabaseAdmin ?? supabase
  const { error } = await client.from('app_equipment_types').delete().eq('id', id)
  if (error) throw error
}

export async function createEquipmentSerial(payload: EquipmentSerialCreateInput) {
  const client = supabaseAdmin ?? supabase
  const { error } = await client.from('app_equipment_serials').insert({
    equipment_type_id: payload.equipmentTypeId,
    serial: payload.serial,
    barcode: payload.barcode ?? null,
    status: payload.status,
    condition: payload.condition,
    notes: payload.notes,
  })
  if (error) throw error
}

export async function deleteEquipmentSerials(serialIds: number[]) {
  const ids = Array.from(new Set(serialIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))))
  if (!ids.length) return

  const client = supabaseAdmin ?? supabase
  const { error } = await client.from('app_equipment_serials').delete().in('id', ids)
  if (error) throw error
}

function sanitizeAssetsForPersist(assets: EquipmentTypeAsset[] = [], equipmentTypeId?: number) {
  return assets
    .filter((asset) => {
      const filePath = String(asset.filePath || '').trim()
      return Boolean(filePath) && !asset.isPending
    })
    .map((asset, index) => ({
      equipmentTypeId,
      assetType: asset.assetType,
      filePath: String(asset.filePath || '').trim(),
      displayOrder: asset.displayOrder ?? index,
      metadata: asset.metadata ?? {},
    }))
}

export async function upsertEquipmentSpecs(
  equipmentTypeId: number,
  specs: EquipmentTypeSpecs | undefined,
) {
  if (!specs) return
  const client = supabaseAdmin ?? supabase
  const payload = {
    shipping_weight_oz: specs.shippingWeightOz,
    shipping_height_in: specs.shippingHeightIn,
    shipping_width_in: specs.shippingWidthIn,
    shipping_depth_in: specs.shippingDepthIn,
    specs: specs.metadata ?? {},
  }
  const { error } = await client
    .from('app_equipment_type_specs')
    .upsert({ equipment_type_id: equipmentTypeId, ...payload }, { onConflict: 'equipment_type_id' })
  if (error) throw error
}

export async function replaceEquipmentAssets(
  equipmentTypeId: number,
  assets: EquipmentTypeAsset[] = [],
) {
  const client = supabaseAdmin ?? supabase
  const { error: deleteError } = await client
    .from('app_equipment_type_assets')
    .delete()
    .eq('equipment_type_id', equipmentTypeId)
  if (deleteError) throw deleteError

  const sanitizedAssets = sanitizeAssetsForPersist(assets, equipmentTypeId)
  if (!sanitizedAssets.length) return

  const { error: insertError } = await client.from('app_equipment_type_assets').insert(
    sanitizedAssets.map((asset) => ({
      equipment_type_id: equipmentTypeId,
      asset_type: asset.assetType,
      file_path: asset.filePath,
      display_order: asset.displayOrder,
      metadata: asset.metadata,
    })),
  )
  if (insertError) throw insertError
}

export async function replaceEquipmentAccessories(
  equipmentTypeId: number,
  accessories: EquipmentTypeAccessory[] = [],
) {
  const client = supabaseAdmin ?? supabase
  const { error: deleteError } = await client
    .from('app_equipment_type_accessories')
    .delete()
    .eq('equipment_type_id', equipmentTypeId)
  if (deleteError) throw deleteError

  if (!accessories.length) return

  const { error: insertError } = await client.from('app_equipment_type_accessories').insert(
    accessories.map((item) => ({
      equipment_type_id: equipmentTypeId,
      accessory_for_type_id: item.accessoryForTypeId,
      notes: item.notes ?? null,
    })),
  )
  if (insertError) throw insertError
}

export async function updateEquipmentSerialStatus(
  serialId: number,
  change: EquipmentSerialStatusChange,
) {
  const client = supabaseAdmin ?? supabase
  const { error } = await client.rpc('equipment_serial_change_status', {
    p_serial_id: serialId,
    p_from_status: change.fromStatus,
    p_to_status: change.toStatus,
    p_context: change.context ?? {},
  })
  if (error) throw error
}

export async function createEquipmentTypeWithDetails(payload: EquipmentTypeCreateInput) {
  const client = supabaseAdmin ?? supabase
  const { data, error } = await client
    .from('app_equipment_types')
    .insert({
      name: payload.name,
      make: payload.make,
      model: payload.model,
      descriptor: payload.descriptor ?? null,
      category_id: payload.categoryId ?? null,
      is_accessory: payload.isAccessory ?? false,
      status: payload.status ?? 'active',
      products: payload.products ?? ['POTS Repl'],
    })
    .select('id')
    .single()
  if (error) throw error

  const equipmentTypeId = data?.id
  if (!equipmentTypeId) throw new Error('Failed to create equipment type')

  const sanitizedAssets = sanitizeAssetsForPersist(payload.assets ?? [], equipmentTypeId)
  await upsertEquipmentSpecs(equipmentTypeId, payload.specs)
  await replaceEquipmentAssets(equipmentTypeId, sanitizedAssets)
  await replaceEquipmentAccessories(
    equipmentTypeId,
    (payload.accessories ?? []).map((item) => ({
      equipmentTypeId,
      accessoryForTypeId: item.accessoryForTypeId,
      notes: item.notes ?? null,
    })),
  )

  return equipmentTypeId
}

export async function updateEquipmentTypeWithDetails(id: number, payload: EquipmentTypeCreateInput) {
  await updateEquipmentType(id, {
    name: payload.name,
    make: payload.make,
    model: payload.model,
    descriptor: payload.descriptor ?? null,
    categoryId: payload.categoryId ?? null,
    isAccessory: payload.isAccessory ?? false,
    status: payload.status ?? 'active',
    products: payload.products ?? ['POTS Repl'],
  })

  const sanitizedAssets = sanitizeAssetsForPersist(payload.assets ?? [], id)
  await upsertEquipmentSpecs(id, payload.specs)
  await replaceEquipmentAssets(id, sanitizedAssets)
  await replaceEquipmentAccessories(
    id,
    (payload.accessories ?? []).map((item) => ({
      equipmentTypeId: id,
      accessoryForTypeId: item.accessoryForTypeId,
      notes: item.notes ?? null,
    })),
  )
}

export async function listEquipmentProducts(): Promise<EquipmentProduct[]> {
  const { data, error } = await supabase
    .from('app_products')
    .select('id, code, name, description, is_active')
    .order('name')
  if (error) throw error
  return (data ?? []).map((row) => ({
    id: Number(row.id),
    code: row.code,
    name: row.name,
    description: row.description ?? null,
    isActive: Boolean(row.is_active ?? true),
  }))
}

export async function findEquipmentTypeByIdentity(
  make: string,
  model: string,
  descriptor?: string | null,
): Promise<EquipmentTypeSummary | null> {
  const { data, error } = await supabase
    .from('equipment_types_v')
    .select(
      'equipment_type_id, type_key, name, make, model, descriptor, category_id, category_name, is_accessory, status, products, specs, assets, accessories, available_count, reserved_count, checked_out_count, maintenance_count, retired_count, lost_count, total_count',
    )
    .eq('make', make)
    .eq('model', model)
    .eq('descriptor', descriptor ?? null)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  return mapType(data as EquipmentTypeRow)
}

export async function bulkCreateSerials(
  equipmentTypeId: number,
  serials: string[],
  defaultStatus: EquipmentStatus = 'available',
): Promise<number> {
  const cleaned = Array.from(new Set(serials.map((s) => s.trim()).filter(Boolean)))
  if (!cleaned.length) return 0

  const rows = cleaned.map((serial) => ({
    equipment_type_id: equipmentTypeId,
    serial,
    status: defaultStatus,
  }))
  const client = supabaseAdmin ?? supabase
  const { error } = await client.from('app_equipment_serials').insert(rows)
  if (error) {
    if ((error as any).code === '23505') {
      throw new Error('One or more serial numbers already exist. Nothing was imported.')
    }
    throw error
  }
  return cleaned.length
}

export async function deleteEquipmentProduct(id: number) {
  const client = supabaseAdmin ?? supabase
  const { error } = await client.from('app_products').delete().eq('id', id)
  if (error) throw error
}

export async function listEquipmentDrafts(): Promise<EquipmentTypeDraft[]> {
  const { data, error } = await supabase.from('app_equipment_type_drafts').select('*').order('updated_at', { ascending: false })
  if (error) throw error
  return (data as EquipmentTypeDraft[] | null) ?? []
}

export async function upsertEquipmentDraft(payload: EquipmentTypeDraftInput & { id?: number }) {
  const client = supabaseAdmin ?? supabase
  const base = {
    name: payload.name,
    make: payload.make,
    model: payload.model,
    descriptor: payload.descriptor ?? null,
    category_id: payload.categoryId ?? null,
    is_accessory: payload.isAccessory ?? false,
    products: payload.products ?? ['POTS Repl'],
    specs: payload.specs ?? {},
    assets: payload.assets ?? [],
    accessories: payload.accessories ?? [],
    progress_step: payload.progressStep ?? 'core',
  }
  let response
  if (payload.id) {
    response = await client
      .from('app_equipment_type_drafts')
      .update({ ...base, updated_at: new Date().toISOString() })
      .eq('id', payload.id)
      .select('*')
      .single()
  } else {
    response = await client.from('app_equipment_type_drafts').insert(base).select('*').single()
  }
  if (response.error) throw response.error
  return response.data as EquipmentTypeDraft
}

export async function deleteEquipmentDraft(id: number) {
  const client = supabaseAdmin ?? supabase
  const { error } = await client.from('app_equipment_type_drafts').delete().eq('id', id)
  if (error) throw error
}

export async function createEquipmentProduct(
  payload: EquipmentProductCreateInput,
): Promise<EquipmentProduct> {
  const client = supabaseAdmin ?? supabase
  const { data, error } = await client
    .from('app_products')
    .insert({
      code: payload.code,
      name: payload.name,
      description: payload.description ?? null,
      is_active: payload.isActive ?? true,
    })
    .select('id, code, name, description, is_active')
    .single()
  if (error) throw error
  return {
    id: Number(data.id),
    code: data.code,
    name: data.name,
    description: data.description ?? null,
    isActive: Boolean(data.is_active ?? true),
  }
}

export async function updateEquipmentProduct(
  id: number,
  patch: EquipmentProductUpdateInput,
): Promise<EquipmentProduct | null> {
  const client = supabaseAdmin ?? supabase
  const payload: Record<string, unknown> = {}
  if (patch.code !== undefined) payload.code = patch.code
  if (patch.name !== undefined) payload.name = patch.name
  if (patch.description !== undefined) payload.description = patch.description
  if (patch.isActive !== undefined) payload.is_active = patch.isActive

  if (!Object.keys(payload).length) return null

  const { data, error } = await client
    .from('app_products')
    .update(payload)
    .eq('id', id)
    .select('id, code, name, description, is_active')
    .single()
  if (error) throw error
  return {
    id: Number(data.id),
    code: data.code,
    name: data.name,
    description: data.description ?? null,
    isActive: Boolean(data.is_active ?? true),
  }
}

export type BulkImportPayload = {
  make: string
  model: string
  descriptor?: string | null
  is_accessory?: boolean
  products?: string[]
  serials: string[]
  status?: EquipmentStatus
}

export async function bulkImportEquipment(payload: BulkImportPayload[]): Promise<BulkImportResult> {
  const client = supabaseAdmin ?? supabase
  const { data, error } = await client.rpc('bulk_import_equipment', payload)
  if (error) throw error
  return data as BulkImportResult
}

export type BulkImportResult = {
  createdTypes: number
  existingTypes: number
  serialsInserted: number
  serialsSkipped: number
}
